import React from 'react'
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, Text } from '../../components/ui'
import { comercialAccountHealthService } from '../../services/comercialAccountHealth'
import { userLogsService } from '../../services/userLogs'
import type {
  AccountHealthLevel,
  AccountHealthOption,
  AccountHealthPillar,
} from '../../types/comercialAccountHealth'
import {
  sumPillarMaxPoints,
  validateLevelNoOverlap,
  validateLevelRanges,
} from '../../utils/comercialAccountHealth'

type CadastroClientesTabProps = {
  canInsert: boolean
  canUpdate: boolean
  canDelete: boolean
}

type LevelDraft = {
  id: string | null
  label: string
  min_score: string
  max_score: string
}

type PillarDraft = {
  id: string | null
  title: string
  question: string
  max_points: string
}

type OptionDraft = {
  id: string | null
  pillarId: string
  pillarMax: number
  label: string
  emoji: string
  points: string
}

type DeleteTarget =
  | { kind: 'level'; id: string; label: string }
  | { kind: 'pillar'; id: string; label: string }
  | { kind: 'option'; id: string; label: string }

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return 'Erro inesperado ao processar a solicitação.'
}

const CadastroClientesTab: React.FC<CadastroClientesTabProps> = ({
  canInsert,
  canUpdate,
  canDelete,
}) => {
  const [levels, setLevels] = React.useState<AccountHealthLevel[]>([])
  const [pillars, setPillars] = React.useState<AccountHealthPillar[]>([])
  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [saving, setSaving] = React.useState(false)

  const [levelDraft, setLevelDraft] = React.useState<LevelDraft | null>(null)
  const [pillarDraft, setPillarDraft] = React.useState<PillarDraft | null>(null)
  const [optionDraft, setOptionDraft] = React.useState<OptionDraft | null>(null)
  const [deleteTarget, setDeleteTarget] = React.useState<DeleteTarget | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)

  const loadConfig = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const config = await comercialAccountHealthService.listConfig()
      setLevels(config.levels)
      setPillars(config.pillars)
    } catch (error) {
      setPageError(getErrorMessage(error))
      setLevels([])
      setPillars([])
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadConfig()
  }, [loadConfig])

  const maxPointsSum = sumPillarMaxPoints(pillars)
  const rangesWarning = validateLevelRanges(levels)

  const nextSortOrder = (items: { sort_order: number }[]) =>
    items.reduce((max, item) => Math.max(max, item.sort_order), 0) + 1

  const openLevelCreate = () => {
    setFormError(null)
    setLevelDraft({ id: null, label: '', min_score: '0', max_score: '0' })
  }

  const openLevelEdit = (level: AccountHealthLevel) => {
    setFormError(null)
    setLevelDraft({
      id: level.id,
      label: level.label,
      min_score: String(level.min_score),
      max_score: String(level.max_score),
    })
  }

  const openPillarCreate = () => {
    setFormError(null)
    setPillarDraft({ id: null, title: '', question: '', max_points: '10' })
  }

  const openPillarEdit = (pillar: AccountHealthPillar) => {
    setFormError(null)
    setPillarDraft({
      id: pillar.id,
      title: pillar.title,
      question: pillar.question,
      max_points: String(pillar.max_points),
    })
  }

  const openOptionCreate = (pillar: AccountHealthPillar) => {
    setFormError(null)
    setOptionDraft({
      id: null,
      pillarId: pillar.id,
      pillarMax: pillar.max_points,
      label: '',
      emoji: '',
      points: '0',
    })
  }

  const openOptionEdit = (option: AccountHealthOption, pillarMax: number) => {
    setFormError(null)
    setOptionDraft({
      id: option.id,
      pillarId: option.pillar_id,
      pillarMax,
      label: option.label,
      emoji: option.emoji ?? '',
      points: String(option.points),
    })
  }

  const closeModals = () => {
    if (saving) return
    setLevelDraft(null)
    setPillarDraft(null)
    setOptionDraft(null)
    setDeleteTarget(null)
    setFormError(null)
  }

  const saveLevel = async () => {
    if (!levelDraft) return

    const label = levelDraft.label.trim()
    const min_score = parseInt(levelDraft.min_score, 10)
    const max_score = parseInt(levelDraft.max_score, 10)
    if (!label) {
      setFormError('Informe o nome da faixa.')
      return
    }
    if (!Number.isFinite(min_score) || !Number.isFinite(max_score)) {
      setFormError('Mínimo e máximo devem ser números inteiros.')
      return
    }
    if (min_score < 0 || max_score > 100 || min_score > max_score) {
      setFormError('Faixa inválida. Use valores entre 0 e 100, com mínimo ≤ máximo.')
      return
    }

    const nextLevels: AccountHealthLevel[] = levelDraft.id
      ? levels.map((l) =>
          l.id === levelDraft.id ? { ...l, label, min_score, max_score } : l
        )
      : [
          ...levels,
          {
            id: '__new__',
            label,
            min_score,
            max_score,
            sort_order: nextSortOrder(levels),
            created_at: null,
            updated_at: null,
          },
        ]

    const overlapError = validateLevelNoOverlap(nextLevels)
    if (overlapError) {
      setFormError(overlapError)
      return
    }

    setSaving(true)
    setFormError(null)
    try {
      if (levelDraft.id) {
        const original = levels.find((l) => l.id === levelDraft.id)
        if (!original) return
        await comercialAccountHealthService.updateLevel(levelDraft.id, {
          label,
          min_score,
          max_score,
        })
        await userLogsService.logUpdate(
          'parameter',
          levelDraft.id,
          `Alterou faixa de saúde da conta: ${original.label}`,
          {
            label: original.label,
            min_score: original.min_score,
            max_score: original.max_score,
          },
          { label, min_score, max_score },
          { context: 'comercial_cadastro_clientes', parameter_key: original.id }
        )
      } else {
        const created = await comercialAccountHealthService.createLevel({
          label,
          min_score,
          max_score,
          sort_order: nextSortOrder(levels),
        })
        await userLogsService.logCreate(
          'parameter',
          created.id,
          `Criou faixa de saúde da conta: ${label}`,
          {
            context: 'comercial_cadastro_clientes',
            new_values: { label, min_score, max_score },
          }
        )
      }
      closeModals()
      await loadConfig()
    } catch (error) {
      setFormError(getErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  const savePillar = async () => {
    if (!pillarDraft) return

    const title = pillarDraft.title.trim()
    const question = pillarDraft.question.trim()
    const max_points = parseInt(pillarDraft.max_points, 10)
    if (!title) {
      setFormError('Informe o título do pilar.')
      return
    }
    if (!Number.isFinite(max_points) || max_points <= 0 || max_points > 100) {
      setFormError('Peso máximo deve ser um inteiro entre 1 e 100.')
      return
    }

    if (pillarDraft.id) {
      const original = pillars.find((p) => p.id === pillarDraft.id)
      if (!original) return
      const maxOption = Math.max(0, ...original.options.map((o) => o.points))
      if (maxOption > max_points) {
        setFormError(
          `Há opções com até ${maxOption} pontos. Ajuste as opções ou aumente o peso do pilar.`
        )
        return
      }
    }

    setSaving(true)
    setFormError(null)
    try {
      if (pillarDraft.id) {
        const original = pillars.find((p) => p.id === pillarDraft.id)
        if (!original) return
        await comercialAccountHealthService.updatePillar(pillarDraft.id, {
          title,
          question,
          max_points,
        })
        await userLogsService.logUpdate(
          'parameter',
          pillarDraft.id,
          `Alterou pilar de saúde da conta: ${original.title}`,
          {
            title: original.title,
            question: original.question,
            max_points: original.max_points,
          },
          { title, question, max_points },
          { context: 'comercial_cadastro_clientes', parameter_key: original.id }
        )
      } else {
        const created = await comercialAccountHealthService.createPillar({
          title,
          question,
          max_points,
          sort_order: nextSortOrder(pillars),
        })
        await userLogsService.logCreate(
          'parameter',
          created.id,
          `Criou pilar de saúde da conta: ${title}`,
          {
            context: 'comercial_cadastro_clientes',
            new_values: { title, question, max_points },
          }
        )
      }
      closeModals()
      await loadConfig()
    } catch (error) {
      setFormError(getErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  const saveOption = async () => {
    if (!optionDraft) return
    const pillar = pillars.find((p) => p.id === optionDraft.pillarId)
    if (!pillar) return

    const label = optionDraft.label.trim()
    const emoji = optionDraft.emoji.trim() || null
    const points = parseInt(optionDraft.points, 10)
    if (!label) {
      setFormError('Informe o rótulo da opção.')
      return
    }
    if (!Number.isFinite(points) || points < 0 || points > optionDraft.pillarMax) {
      setFormError(`Pontos devem ser um inteiro entre 0 e ${optionDraft.pillarMax}.`)
      return
    }

    setSaving(true)
    setFormError(null)
    try {
      if (optionDraft.id) {
        const original = pillar.options.find((o) => o.id === optionDraft.id)
        if (!original) return
        await comercialAccountHealthService.updateOption(optionDraft.id, {
          label,
          emoji,
          points,
        })
        await userLogsService.logUpdate(
          'parameter',
          optionDraft.id,
          `Alterou opção do pilar ${pillar.title}: ${label}`,
          {
            label: original.label,
            emoji: original.emoji,
            points: original.points,
          },
          { label, emoji, points },
          {
            context: 'comercial_cadastro_clientes',
            parameter_key: optionDraft.id,
          }
        )
      } else {
        const created = await comercialAccountHealthService.createOption({
          pillar_id: optionDraft.pillarId,
          label,
          emoji,
          points,
          sort_order: nextSortOrder(pillar.options),
        })
        await userLogsService.logCreate(
          'parameter',
          created.id,
          `Criou opção no pilar ${pillar.title}: ${label}`,
          {
            context: 'comercial_cadastro_clientes',
            new_values: { pillar_id: optionDraft.pillarId, label, emoji, points },
          }
        )
      }
      closeModals()
      await loadConfig()
    } catch (error) {
      setFormError(getErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setSaving(true)
    setFormError(null)
    try {
      if (deleteTarget.kind === 'level') {
        await comercialAccountHealthService.deleteLevel(deleteTarget.id)
        await userLogsService.logDelete(
          'parameter',
          deleteTarget.id,
          `Excluiu faixa de saúde da conta: ${deleteTarget.label}`,
          { context: 'comercial_cadastro_clientes' }
        )
      } else if (deleteTarget.kind === 'pillar') {
        await comercialAccountHealthService.deletePillar(deleteTarget.id)
        await userLogsService.logDelete(
          'parameter',
          deleteTarget.id,
          `Excluiu pilar de saúde da conta: ${deleteTarget.label}`,
          { context: 'comercial_cadastro_clientes' }
        )
      } else {
        await comercialAccountHealthService.deleteOption(deleteTarget.id)
        await userLogsService.logDelete(
          'parameter',
          deleteTarget.id,
          `Excluiu opção de saúde da conta: ${deleteTarget.label}`,
          { context: 'comercial_cadastro_clientes' }
        )
      }
      closeModals()
      await loadConfig()
    } catch (error) {
      setFormError(getErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  const thClass =
    'px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'
  const thActionsClass =
    'px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'
  const actionBtnClass =
    'text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors'
  const deleteBtnClass =
    'text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors'

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {pageError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{pageError}</Text>
        </div>
      )}

      <div>
        <Text size="sm" color="muted">
          Defina as faixas de status (0–100) e os pilares da pesquisa de Saúde da Conta.
        </Text>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
          <Text size="sm" color="muted">
            Soma dos pesos: {maxPointsSum}/100
            {maxPointsSum !== 100 ? ' (recomendado = 100)' : ''}
          </Text>
          {rangesWarning && (
            <Text size="sm" className="text-amber-700 dark:text-amber-400">
              {rangesWarning}
            </Text>
          )}
        </div>
      </div>

      {/* Faixas */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
              Faixas de status
            </Heading>
            <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
              {levels.length} {levels.length === 1 ? 'registro' : 'registros'}
            </span>
          </div>
          {canInsert && (
            <Button variant="solid" size="md" onClick={openLevelCreate}>
              <PlusIcon className="h-4 w-4" />
              Nova faixa
            </Button>
          )}
        </div>

        {levels.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Text size="sm" color="muted">
              Nenhuma faixa cadastrada. Execute o SQL comercial_17 ou crie uma nova faixa.
            </Text>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className={thClass}>Status</th>
                  <th className={thClass}>Mínimo</th>
                  <th className={thClass}>Máximo</th>
                  {(canUpdate || canDelete) && <th className={thActionsClass}>Ações</th>}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {levels.map((level) => (
                  <tr
                    key={level.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {level.label}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {level.min_score}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {level.max_score}
                    </td>
                    {(canUpdate || canDelete) && (
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {canUpdate && (
                            <button
                              type="button"
                              title="Editar"
                              onClick={() => openLevelEdit(level)}
                              className={actionBtnClass}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              title="Excluir"
                              onClick={() =>
                                setDeleteTarget({
                                  kind: 'level',
                                  id: level.id,
                                  label: level.label,
                                })
                              }
                              className={deleteBtnClass}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pilares */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
            Pilares
          </Heading>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {pillars.length} {pillars.length === 1 ? 'registro' : 'registros'}
          </span>
        </div>
        {canInsert && (
          <Button variant="solid" size="md" onClick={openPillarCreate}>
            <PlusIcon className="h-4 w-4" />
            Novo pilar
          </Button>
        )}
      </div>

      {pillars.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-6 py-12 text-center">
          <Text size="sm" color="muted">
            Nenhum pilar cadastrado.
          </Text>
        </div>
      ) : (
        pillars.map((pillar) => (
          <div
            key={pillar.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    {pillar.title}
                  </Heading>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    até {pillar.max_points} pts · {pillar.options.length}{' '}
                    {pillar.options.length === 1 ? 'opção' : 'opções'}
                  </span>
                </div>
                <Text size="sm" color="muted" className="mt-1">
                  {pillar.question || '—'}
                </Text>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {canInsert && (
                  <Button variant="outline" size="sm" onClick={() => openOptionCreate(pillar)}>
                    <PlusIcon className="h-4 w-4" />
                    Opção
                  </Button>
                )}
                {canUpdate && (
                  <button
                    type="button"
                    title="Editar pilar"
                    onClick={() => openPillarEdit(pillar)}
                    className={actionBtnClass}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    title="Excluir pilar"
                    onClick={() =>
                      setDeleteTarget({
                        kind: 'pillar',
                        id: pillar.id,
                        label: pillar.title,
                      })
                    }
                    className={deleteBtnClass}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {pillar.options.length === 0 ? (
              <div className="px-6 py-8 text-center">
                <Text size="sm" color="muted">
                  Nenhuma opção neste pilar.
                </Text>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className={thClass}>Opção</th>
                      <th className={thClass}>Emoji</th>
                      <th className={thClass}>Pontos</th>
                      {(canUpdate || canDelete) && <th className={thActionsClass}>Ações</th>}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {pillar.options.map((option) => (
                      <tr
                        key={option.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {option.label}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {option.emoji || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {option.points}
                        </td>
                        {(canUpdate || canDelete) && (
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              {canUpdate && (
                                <button
                                  type="button"
                                  title="Editar"
                                  onClick={() => openOptionEdit(option, pillar.max_points)}
                                  className={actionBtnClass}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  type="button"
                                  title="Excluir"
                                  onClick={() =>
                                    setDeleteTarget({
                                      kind: 'option',
                                      id: option.id,
                                      label: option.label,
                                    })
                                  }
                                  className={deleteBtnClass}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))
      )}

      {(levelDraft || pillarDraft || optionDraft) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
                {levelDraft && (levelDraft.id ? 'Editar faixa de status' : 'Nova faixa de status')}
                {pillarDraft && (pillarDraft.id ? 'Editar pilar' : 'Novo pilar')}
                {optionDraft && (optionDraft.id ? 'Editar opção' : 'Nova opção')}
              </Text>
              <Button variant="ghost" onClick={closeModals} disabled={saving}>
                Fechar
              </Button>
            </div>

            {levelDraft && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nome <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={levelDraft.label}
                    onChange={(e) => setLevelDraft({ ...levelDraft, label: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Mínimo
                    </label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={levelDraft.min_score}
                      onChange={(e) =>
                        setLevelDraft({ ...levelDraft, min_score: e.target.value })
                      }
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Máximo
                    </label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={levelDraft.max_score}
                      onChange={(e) =>
                        setLevelDraft({ ...levelDraft, max_score: e.target.value })
                      }
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {pillarDraft && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Título <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={pillarDraft.title}
                    onChange={(e) => setPillarDraft({ ...pillarDraft, title: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Pergunta
                  </label>
                  <Input
                    value={pillarDraft.question}
                    onChange={(e) =>
                      setPillarDraft({ ...pillarDraft, question: e.target.value })
                    }
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Peso máximo
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={pillarDraft.max_points}
                    onChange={(e) =>
                      setPillarDraft({ ...pillarDraft, max_points: e.target.value })
                    }
                    className="w-full"
                  />
                </div>
              </div>
            )}

            {optionDraft && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Rótulo <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={optionDraft.label}
                    onChange={(e) => setOptionDraft({ ...optionDraft, label: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Emoji
                    </label>
                    <Input
                      value={optionDraft.emoji}
                      onChange={(e) => setOptionDraft({ ...optionDraft, emoji: e.target.value })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Pontos (máx. {optionDraft.pillarMax})
                    </label>
                    <Input
                      type="number"
                      min={0}
                      max={optionDraft.pillarMax}
                      value={optionDraft.points}
                      onChange={(e) =>
                        setOptionDraft({ ...optionDraft, points: e.target.value })
                      }
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {formError && (
              <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                <Text size="sm">{formError}</Text>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="ghost" onClick={closeModals} disabled={saving}>
                Cancelar
              </Button>
              <Button
                variant="solid"
                disabled={saving}
                onClick={() => {
                  if (levelDraft) void saveLevel()
                  else if (pillarDraft) void savePillar()
                  else if (optionDraft) void saveOption()
                }}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Confirmar Exclusão
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Você está excluindo:{' '}
              <strong className="text-gray-900 dark:text-white">{deleteTarget.label}</strong>
              {deleteTarget.kind === 'pillar' && (
                <>
                  <br />
                  As opções deste pilar também serão removidas.
                </>
              )}
            </Text>
            {formError && (
              <div className="mb-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                <Text size="sm">{formError}</Text>
              </div>
            )}
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="ghost" onClick={closeModals} disabled={saving}>
                Cancelar
              </Button>
              <button
                type="button"
                onClick={() => void confirmDelete()}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {saving ? 'Excluindo...' : 'Confirmar Exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CadastroClientesTab
